import { useEffect, useState, useContext } from 'react';
import './FullPageLoader.css';
import { useLoading } from '../../contexts/LoadingContext';
import { ThemeContext } from '../../contexts/ThemeContext';
import packageJson from '../../../package.json';

const FullPageLoader = () => {
  const { isLoading } = useLoading();
  const { theme } = useContext(ThemeContext);
  const [visible, setVisible] = useState(true);
  const [displayProgress, setDisplayProgress] = useState(0);
  const [startTime] = useState(Date.now());
  const [loadingStage, setLoadingStage] = useState('initial');
  const currentYear = new Date().getFullYear();
  const version = packageJson.version;

  useEffect(() => {
    if (visible) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev || '';
      };
    }
  }, [visible]);

  useEffect(() => {
    if (isLoading) {
      setVisible(true);
      setDisplayProgress((prev) => (prev === 0 ? 5 : 0));
      setLoadingStage('data');
    } else {
      setLoadingStage('rendering');
      const updateProgressiveProgress = () => {
        const heroImage = document.querySelector('.hero-image');
        const heroTitle = document.querySelector('.hero-title');
        const lazyComponents = document.querySelectorAll('[data-lazy-loaded]');

        let currentProgress = 70;

        if (heroImage && heroImage.complete && heroImage.naturalHeight !== 0) {
          currentProgress += 20;
        }

        if (heroTitle && heroTitle.textContent.trim().length > 0) {
          currentProgress += 10;
        }

        if (
          lazyComponents.length > 0 ||
          document.querySelector(
            '.skills-container, .projects-container, .experience-container'
          )
        ) {
          currentProgress = Math.min(100, currentProgress + 20);
        }

        currentProgress = Math.min(100, currentProgress);
        setDisplayProgress(currentProgress);

        if (currentProgress >= 100) {
          const elapsed = Date.now() - startTime;
          const minDisplayTime = 6000;
          const remainingDelay = Math.max(0, minDisplayTime - elapsed);

          setTimeout(() => {
            setVisible(false);
            setDisplayProgress(0);
            window.dispatchEvent(new Event('loadingComplete'));
          }, remainingDelay);
        } else {
          setTimeout(updateProgressiveProgress, 600);
        }
      };

      updateProgressiveProgress();
    }
  }, [isLoading, startTime]);

  useEffect(() => {
    if (loadingStage === 'data' && isLoading) {
      const currentProgress = displayProgress;

      if (currentProgress < 70) {
        const timer = setTimeout(() => {
          let newProgress;
          const step = 70 - currentProgress;

          if (Math.abs(step) > 50) {
            newProgress = currentProgress + step * 0.08;
          } else if (Math.abs(step) > 20) {
            newProgress = currentProgress + step * 0.12;
          } else if (Math.abs(step) > 5) {
            newProgress = currentProgress + step * 0.25;
          } else if (Math.abs(step) > 1) {
            newProgress = currentProgress + step * 0.5;
          } else {
            newProgress = currentProgress + step * 0.75;
          }

          if (newProgress < 1 && currentProgress < 1) {
            newProgress = 1;
          }

          setDisplayProgress(Math.min(70, Math.max(0, newProgress)));
        }, 600);
        return () => clearTimeout(timer);
      }
    }
  }, [isLoading, displayProgress, loadingStage]);

  if (!visible) return null;

  return (
    <div className={`fp-loader-overlay ${theme}`} aria-hidden={!visible}>
      <div className="fp-loader-content">
        <svg
          className="fp-circle"
          viewBox="0 0 100 100"
          aria-hidden="true"
          style={{
            width:
              window.innerWidth <= 320
                ? '90px'
                : window.innerWidth <= 768
                  ? '100px'
                  : '120px',
            height:
              window.innerWidth <= 320
                ? '90px'
                : window.innerWidth <= 768
                  ? '100px'
                  : '120px',
            transition: 'width 6s ease-out',
          }}
        >
          <circle cx="50" cy="50" r="45" className="fp-circle-bg" />
          <circle
            cx="50"
            cy="50"
            r="45"
            className="fp-circle-fg"
            style={{
              transition: 'stroke-dashoffset 6s ease-out',
              strokeDashoffset: `${440 - (440 * displayProgress) / 100}`,
            }}
          />
        </svg>
        <div className="fp-loader-text">
          <div>{`© ${currentYear} All Rights Reserved`}</div>
          <div>{`جميع الحقوق محفوظة\nv${version}`}</div>
        </div>
      </div>
    </div>
  );
};

export default FullPageLoader;
